import { useEffect } from 'react';
import { GetServerSideProps } from 'next';
import { componentFactory } from 'temp/componentFactory';

import { ComponentPropsContext, handleEditorFastRefresh, SitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

import { ModalProvider } from 'src/context/Modal/ModalProvider';
import { RcnUserProvider } from 'src/context/RcnUser/RcnUserProvider';
import { WildCardRouteProvider } from 'src/context/WildCardRoute/WildCardRouteProvider';

import { sitecorePagePropsFactory } from 'src/lib/page-props-factory';
import { SitecorePageProps } from 'src/lib/SitecorePageProps';
import { SitecoreContextValue } from 'lib/ComponentProps';

import { NotFound } from 'src/components/errors/_NotFound/_NotFound';
import Layout from 'src/components/layout/Layout/Layout';

import ErrorBoundary from '../components/errors/ErrorBoundary/ErrorBoundary';

export default function SitecorePage({ notFound, componentProps, sitecoreContext }: SitecorePageProps): JSX.Element {
	useEffect(() => {
		// Since Sitecore editors do not support Fast Refresh, need to refresh EE chromes after Fast Refresh finished
		handleEditorFastRefresh();
	}, []);

	if (notFound || !sitecoreContext?.route) {
		// Shouldn't hit this (as long as 'notFound' is being returned below), but just to be safe
		return <NotFound />;
	}

	return (
		<ComponentPropsContext value={componentProps}>
			<SitecoreContext<SitecoreContextValue> componentFactory={componentFactory} context={sitecoreContext}>
				<RcnUserProvider>
					<WildCardRouteProvider>
						<ModalProvider>
							<ErrorBoundary>
								<Layout />
							</ErrorBoundary>
						</ModalProvider>
					</WildCardRouteProvider>
				</RcnUserProvider>
			</SitecoreContext>
		</ComponentPropsContext>
	);
}

// This function gets called at request time on server-side.
export const getServerSideProps: GetServerSideProps = async (context) => {
	const props = await sitecorePagePropsFactory.create(context);

	if (props.sitecoreContext) {
		props.sitecoreContext.referer = context.req.headers.referer ? context.req.headers.referer : '';
		props.sitecoreContext.host = context.req.headers.host ? context.req.headers.host : '';
	}

	return {
		props,
		// RCN Customisation - ensures that 404 status code given on wild card pages that don't exist
		notFound: !props.sitecoreContext?.wildcardMetaData && props.sitecoreContext?.route?.name === '*' ? true : props.notFound,
	};
};
